import React from "react";
import { useForm } from "react-hook-form";
import { Form, FormActions, FormDropdown, FormFields, FormTextField, Stack } from "sprint-common-web";

import { EncodeSpecificationMpeg4ListingItem } from "../../EncodeSpecificationListingItem";
import { commandTextFieldTooltipContent } from "../common/commandTextFieldTooltipContent";
import { useFfmpegCommandValidation } from "../common/useFfmpegCommandValidation";
import { useEditEncodeSpecificationForm } from "./useEditEncodeSpecificationForm";

export interface EditEncodeSpecificationMpeg4FormProps {
    item: EncodeSpecificationMpeg4ListingItem;
    onCancel(): void;
    onSubmit(): void;
}

export interface EditEncodeSpecificationMpeg4FormInputs {
    name: string;
    ffmpegCommand: string;
    framerateProfileId: string;
    targetAspectRatioId: string;
}

export const EditEncodeSpecificationMpeg4Form: React.FC<EditEncodeSpecificationMpeg4FormProps> = (
    props: EditEncodeSpecificationMpeg4FormProps
) => {
    const { item, onCancel, onSubmit } = props;

    const { errors, formState, control, reset, handleSubmit } = useForm<EditEncodeSpecificationMpeg4FormInputs>({
        defaultValues: {
            name: item.name,
            ffmpegCommand: item.details.ffmpegCommand,
            framerateProfileId: item.details.framerateProfile.id,
            targetAspectRatioId: item.targetAspectRatio.id
        },
        criteriaMode: "all"
    });

    const {
        framerateProfileOptions,
        aspectRatioOptions,
        validateNameUnique,
        editEncodeSpecMpeg4
    } = useEditEncodeSpecificationForm();

    const { validateFfmpegCommand } = useFfmpegCommandValidation();

    const handleEditEncodeSpecification = async (inputs: EditEncodeSpecificationMpeg4FormInputs) => {
        await editEncodeSpecMpeg4(item.id, inputs);
        onSubmit();
        reset();
    };

    const handleCancelButtonClick = () => {
        reset();
        onCancel();
    };

    return (
        <Stack>
            <Form onSubmit={handleSubmit(handleEditEncodeSpecification)}>
                <Stack verticalAlign="space-between">
                    <FormFields>
                        <FormTextField
                            label="Name"
                            control={control}
                            name="name"
                            errors={errors}
                            rules={{
                                required: "Name is required field",
                                validate: (name) => validateNameUnique(name, item.id)
                            }}
                        />
                        <FormTextField
                            label="Ffmpeg command"
                            control={control}
                            name="ffmpegCommand"
                            errors={errors}
                            multiline={true}
                            height={200}
                            rules={{
                                required: "Ffmpeg command is required field",
                                validate: {
                                    validateFfmpegCommand
                                }
                            }}
                            tooltipContent={commandTextFieldTooltipContent}
                        />
                        <FormDropdown
                            options={framerateProfileOptions}
                            label="Framerate profile"
                            name="framerateProfileId"
                            control={control}
                            errors={errors}
                            rules={{ required: "Select framerate profile" }}
                        />
                        <FormDropdown
                            label={"Target aspect ratio"}
                            name={"targetAspectRatioId"}
                            control={control}
                            errors={errors}
                            options={aspectRatioOptions}
                            rules={{
                                required: "Select target aspect ratio"
                            }}
                        />
                    </FormFields>
                    <FormActions
                        isLoading={formState.isSubmitting || formState.isValidating}
                        onCancel={handleCancelButtonClick}
                    />
                </Stack>
            </Form>
        </Stack>
    );
};
